// SecuritySettings.tsx
// Phase 6 (2026-05-25) — Security Settings UI. Frontend-only for now;
// wires to live backend in Phase 17 when the staged
// `auth_security.py` module is activated. This UI demonstrates the
// surface for: forgot password, MFA setup, backup codes, session
// timeout, login audit, account lockout.
//
// Hard rules:
//   - No secrets shown in the rendered UI.
//   - Backup codes shown ONCE on initial reveal then masked.
//   - "MFA Required for Paper API" indicator visible (Phase 7 gate).
import React, { useState } from 'react';
import { Lock, ShieldCheck, KeyRound, AlertTriangle, Activity } from 'lucide-react';
import { useAuthenticatedUser } from '../hooks/useAuthenticatedUser';
import { MockupPreviewBanner } from './MockupPreviewBanner';

const masked = '•••••• •••••• ••••••';

export default function SecuritySettings() {
  const u = useAuthenticatedUser();
  const [resetSent, setResetSent] = useState(false);
  const [mfaPhase, setMfaPhase] = useState<'idle' | 'setup' | 'verified'>('idle');
  const [backupCodesShown, setBackupCodesShown] = useState(false);

  // Demo display only — real codes come from backend POST /api/v1/auth/mfa/setup
  // which returns base32 secret + 10 backup codes ONCE. UI must mask afterward.
  // For now, render placeholders that demonstrate the UX.
  const demoOtpauthSecret = 'JBSWY3DPEHPK3PXP'; // RFC 4226 example secret only — never used for real verification
  const demoBackupCodes = backupCodesShown
    ? ['11111-AAAAA', '22222-BBBBB', '33333-CCCCC', '44444-DDDDD', '55555-EEEEE',
       '66666-FFFFF', '77777-GGGGG', '88888-HHHHH', '99999-IIIII', '00000-JJJJJ']
    : Array.from({ length: 10 }, () => masked);

  return (
    <div className="space-y-6" data-testid="security-settings">
      <MockupPreviewBanner screen='security-settings' />
      {/* MFA section */}
      <section className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-6 shadow-sm">
        <div className="flex items-center gap-2 mb-4">
          <ShieldCheck size={18} className="text-emerald-600" />
          <h3 className="text-lg font-black uppercase italic">Multi-Factor Authentication</h3>
        </div>
        <p className="text-xs text-[var(--muted-text)] font-bold mb-4">
          MFA must be enabled before a paper-trading API key can be connected. Live trading remains permanently disabled at this layer.
        </p>
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-3 text-xs font-bold text-amber-700 dark:text-amber-300 mb-4"
             data-testid="mfa-required-banner">
          MFA required before Paper API connection (Phase 7 gate).
        </div>

        {mfaPhase === 'idle' && (
          <button
            data-testid="mfa-setup-button"
            onClick={() => setMfaPhase('setup')}
            className="rounded-xl bg-emerald-600 hover:bg-emerald-500 px-4 py-2 text-xs font-black uppercase italic text-white"
          >
            Set up Authenticator
          </button>
        )}
        {mfaPhase === 'setup' && (
          <div className="space-y-3" data-testid="mfa-setup-panel">
            <div className="rounded-xl bg-[var(--input-bg)] border border-[var(--input-border)] p-3">
              <div className="text-[10px] font-black uppercase tracking-widest text-[var(--muted-text)] mb-1">Manual entry secret</div>
              <code className="font-mono text-xs text-[var(--app-text)]" data-testid="mfa-secret-display">{demoOtpauthSecret}</code>
              <div className="text-[10px] mt-1 text-[var(--muted-text)] font-bold">
                Scan a QR code in production. Demo secret shown for layout only.
              </div>
            </div>
            <label className="block">
              <span className="block mb-1 text-xs font-black uppercase tracking-tight italic text-[var(--app-text)]">Enter 6-digit code</span>
              <input
                data-testid="mfa-code-input"
                type="text"
                inputMode="numeric"
                maxLength={6}
                placeholder="123456"
                className="w-32 rounded-xl border border-[var(--input-border)] bg-[var(--input-bg)] px-3 py-2 text-sm tabular-nums"
              />
            </label>
            <button
              data-testid="mfa-verify-button"
              onClick={() => setMfaPhase('verified')}
              className="rounded-xl bg-emerald-600 hover:bg-emerald-500 px-4 py-2 text-xs font-black uppercase italic text-white"
            >
              Verify &amp; enable
            </button>
          </div>
        )}
        {mfaPhase === 'verified' && (
          <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-3 text-xs font-bold text-emerald-700"
               data-testid="mfa-enabled-status">
            MFA enabled.
          </div>
        )}
      </section>

      {/* Backup codes section */}
      <section className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-6 shadow-sm">
        <div className="flex items-center gap-2 mb-3">
          <KeyRound size={18} className="text-emerald-600" />
          <h3 className="text-lg font-black uppercase italic">Backup Codes</h3>
        </div>
        <p className="text-xs text-[var(--muted-text)] font-bold mb-3">
          Single-use codes for account recovery if your authenticator is unavailable. Codes are shown once.
        </p>
        <div className="grid grid-cols-2 gap-2 mb-3" data-testid="backup-codes-list">
          {demoBackupCodes.map((c, i) => (
            <code key={i} className="font-mono text-xs px-3 py-2 rounded-lg bg-[var(--input-bg)] border border-[var(--input-border)] text-[var(--app-text)]">
              {c}
            </code>
          ))}
        </div>
        <button
          data-testid="backup-codes-reveal"
          onClick={() => setBackupCodesShown(true)}
          disabled={backupCodesShown}
          className="rounded-xl bg-emerald-600 hover:bg-emerald-500 px-4 py-2 text-xs font-black uppercase italic text-white disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {backupCodesShown ? 'Codes revealed — store securely' : 'Reveal backup codes'}
        </button>
      </section>

      {/* Forgot password */}
      <section className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-6 shadow-sm">
        <div className="flex items-center gap-2 mb-3">
          <Lock size={18} className="text-emerald-600" />
          <h3 className="text-lg font-black uppercase italic">Password Reset</h3>
        </div>
        <p className="text-xs text-[var(--muted-text)] font-bold mb-3">
          Send a single-use reset link to your email address. The link expires in 30 minutes.
        </p>
        {!resetSent ? (
          <button
            data-testid="password-reset-request"
            onClick={() => setResetSent(true)}
            className="rounded-xl bg-emerald-600 hover:bg-emerald-500 px-4 py-2 text-xs font-black uppercase italic text-white"
          >
            Send reset link
          </button>
        ) : (
          <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-3 text-xs font-bold text-emerald-700"
               data-testid="password-reset-sent">
            Reset link sent to your email. The email never contains the token — it contains a one-time link.
          </div>
        )}
      </section>

      {/* Session + audit log */}
      <section className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl p-6 shadow-sm">
        <div className="flex items-center gap-2 mb-3">
          <Activity size={18} className="text-emerald-600" />
          <h3 className="text-lg font-black uppercase italic">Session &amp; Audit</h3>
        </div>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div className="rounded-xl bg-[var(--input-bg)] border border-[var(--input-border)] p-3">
            <div className="text-[10px] font-black uppercase tracking-widest text-[var(--muted-text)]">Session timeout</div>
            <div className="text-sm font-bold text-[var(--app-text)]">60 minutes idle</div>
          </div>
          <div className="rounded-xl bg-[var(--input-bg)] border border-[var(--input-border)] p-3">
            <div className="text-[10px] font-black uppercase tracking-widest text-[var(--muted-text)]">Lockout policy</div>
            <div className="text-sm font-bold text-[var(--app-text)]">5 failed attempts → 15 min lockout</div>
          </div>
        </div>
        <div className="rounded-xl bg-[var(--input-bg)] border border-[var(--input-border)] p-3" data-testid="audit-log-placeholder">
          <div className="text-[10px] font-black uppercase tracking-widest text-[var(--muted-text)] mb-1">Recent events</div>
          <div className="text-xs font-bold text-[var(--app-text)]">
            Audit log wired in Phase 17 (backend activation). Currently shows placeholder.
          </div>
        </div>
      </section>

      <div className="text-[10px] text-[var(--muted-text)] font-bold uppercase tracking-widest"
           data-testid="security-no-live-trading-note">
        Live trading is permanently blocked at this layer regardless of MFA status — user {u.firstName} {u.lastName} ({u.accountStatusLabel}).
      </div>
    </div>
  );
}
